import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams, useParams, useLocation } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import DownloadAppStore from "../../components/DownloadAppStore";
import DownloadGoogle from "../../components/DownloadGoogle";
import logoYellow from "../../assets/images/icons/logoYellow.svg";
import "./DeepLinkPage.scss";

// Device detection utilities
type DeviceType = "ios" | "android" | "desktop";

const detectDevice = (): DeviceType => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  // iOS detection
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return "ios";
  }

  // Android detection
  if (/android/i.test(userAgent)) {
    return "android";
  }

  return "desktop";
};

// Store URLs (production)
const IOS_STORE_URL = "https://apps.apple.com/au/app/fam/id6748098832";
const ANDROID_STORE_URL = "https://play.google.com/store/apps/details?id=life.fam.fam_app&pcampaignid=deep_link";

// Store URLs (staging) -- Android uses internal test link, no referrer support
const IOS_STORE_URL_STAGING = "https://apps.apple.com/au/app/fam/id6759595965";
const ANDROID_STORE_URL_STAGING = "https://play.google.com/apps/internaltest/4700871654548717057";

const CUSTOM_SCHEME = "famapp";
const APP_OPEN_TIMEOUT = 2500; // ms to wait before assuming app isn't installed

const DEFAULT_DOWNLOAD_ANDROID_REFERRER =
  "utm_source=website&utm_medium=app_download&utm_campaign=download_fam";

// Deep link type configurations
interface DeepLinkConfig {
  title: string;
  subtitle: string;
  subtitleDesktop: string;
  subtitleRedirecting: string;
  infoText: string;
  utmMedium: string;
  hideMobileBanner?: boolean;
  mobileOpenTarget?: "app" | "store";
  androidReferrerMode?: "deeplink" | "passthrough";
}

type DeepLinkPageLinkType =
  | "circle-join"
  | "account-access"
  | "activity"
  | "notes"
  | "health-record"
  | "app-download";

const DEEP_LINK_CONFIGS: Record<DeepLinkPageLinkType, DeepLinkConfig> = {
  "circle-join": {
    title: "You've been invited to join a Care Circle",
    subtitle: "Tap the button below to open the FAM app and join the circle.",
    subtitleDesktop: "Scan the QR code with your phone or download the FAM app to accept this invitation.",
    subtitleRedirecting: "Opening FAM app...",
    infoText: "After installing, the app will automatically open to your invitation.",
    utmMedium: "circle_invite",
  },
  "account-access": {
    title: "Access your Care Recipient's Account",
    subtitle: "Tap the button below to open the FAM app and access the account.",
    subtitleDesktop: "Scan the QR code with your phone or download the FAM app to access this account.",
    subtitleRedirecting: "Opening FAM app...",
    infoText: "After installing, the app will automatically open to the account.",
    utmMedium: "account_access",
  },
  "activity": {
    title: "View Activity in FAM",
    subtitle: "Tap the button below to open the FAM app and view this activity.",
    subtitleDesktop: "Scan the QR code with your phone or download the FAM app to view this activity.",
    subtitleRedirecting: "Opening FAM app...",
    infoText: "After installing, the app will automatically open to this activity.",
    utmMedium: "activity_view",
  },
  "notes": {
    title: "View Notes in FAM",
    subtitle: "Tap the button below to open the FAM app and view these notes.",
    subtitleDesktop: "Scan the QR code with your phone or download the FAM app to view these notes.",
    subtitleRedirecting: "Opening FAM app...",
    infoText: "After installing, the app will automatically open to your notes.",
    utmMedium: "notes_view",
  },
  "health-record": {
    title: "View Health Record in FAM",
    subtitle: "Tap the button below to open the FAM app and view this health record.",
    subtitleDesktop: "Scan the QR code with your phone or download the FAM app to view this health record.",
    subtitleRedirecting: "Opening FAM app...",
    infoText: "After installing, the app will automatically open to the health record.",
    utmMedium: "health_record_view",
  },
  "app-download": {
    title: "Download the FAM app",
    subtitle: "Use the buttons below to get FAM on the App Store or Google Play.",
    subtitleDesktop: "Scan the QR code with your phone or use the buttons below to download FAM.",
    subtitleRedirecting: "Taking you to the store...",
    infoText: "FAM helps families coordinate care, stay organised, and support each other.",
    utmMedium: "app_download",
    hideMobileBanner: true,
    mobileOpenTarget: "store",
    androidReferrerMode: "passthrough",
  },
};

interface DeepLinkPageProps {
  linkType: DeepLinkPageLinkType;
}

const DeepLinkPage: React.FC<DeepLinkPageProps> = ({ linkType }) => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const location = useLocation();
  const [device, setDevice] = useState<DeviceType>("desktop");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const hasAttempted = useRef(false);

  const config = DEEP_LINK_CONFIGS[linkType];
  const hideMobileBanner = config.hideMobileBanner ?? false;
  const mobileOpenTarget = config.mobileOpenTarget ?? "app";
  const androidReferrerMode = config.androidReferrerMode ?? "deeplink";

  const isStaging = location.pathname.startsWith("/staging/");
  const iosStoreUrl = isStaging ? IOS_STORE_URL_STAGING : IOS_STORE_URL;
  const androidStoreUrl = isStaging ? ANDROID_STORE_URL_STAGING : ANDROID_STORE_URL;

  // Build the deep link URLs from the current location
  const { fullDeepLink, customSchemeUrl, referrerParams } = useMemo(() => {
    const queryString = searchParams.toString();
    const search = queryString ? `?${queryString}` : "";
    const pathWithQuery = `${location.pathname}${search}`;

    let referrer = "";
    switch (linkType) {
      case "circle-join":
      case "account-access":
      case "app-download":
        referrer = queryString;
        break;
      case "activity":
        referrer = `activity_id=${params.activityId || ""}`;
        break;
      case "notes":
        referrer = `notes_id=${params.notesId || ""}`;
        break;
      case "health-record":
        referrer = `care_recipient_id=${params.careRecipientId || ""}`;
        break;
    }

    return {
      fullDeepLink: `https://fam.life${pathWithQuery}`,
      customSchemeUrl: `${CUSTOM_SCHEME}:/${pathWithQuery}`,
      referrerParams: referrer,
    };
  }, [linkType, searchParams, params, location.pathname]);

  const encodedAndroidReferrer = useMemo(() => {
    if (androidReferrerMode === "passthrough") {
      const raw = searchParams.toString() || DEFAULT_DOWNLOAD_ANDROID_REFERRER;
      return encodeURIComponent(raw);
    }
    return encodeURIComponent(`utm_source=deeplink&utm_medium=${config.utmMedium}&${referrerParams}`);
  }, [androidReferrerMode, searchParams, config.utmMedium, referrerParams]);

  useEffect(() => {
    setDevice(detectDevice());
  }, []);

  const copyToClipboard = useCallback(async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        document.body.removeChild(textArea);
        return true;
      } catch {
        document.body.removeChild(textArea);
        return false;
      }
    }
  }, []);

  const handleOpenApp = useCallback(async () => {
    setIsRedirecting(true);

    // For iOS, copy the deep link to clipboard before attempting to open
    if (device === "ios") {
      await copyToClipboard(fullDeepLink);
    }

    // Use an anchor element click to open the app - this preserves query params
    // unlike iframe.src or window.location.href which can strip them in some browsers
    const link = document.createElement("a");
    link.href = customSchemeUrl;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      if (document.visibilityState !== "hidden") {
        setIsRedirecting(false);
      }
    }, APP_OPEN_TIMEOUT);
  }, [device, fullDeepLink, customSchemeUrl, copyToClipboard]);

  const navigateToStore = useCallback(() => {
    if (device === "ios") {
      copyToClipboard(fullDeepLink);
      window.location.href = iosStoreUrl;
    } else if (device === "android") {
      if (isStaging) {
        window.location.href = androidStoreUrl;
      } else {
        window.location.href = `${androidStoreUrl}&referrer=${encodedAndroidReferrer}`;
      }
    }
  }, [device, fullDeepLink, iosStoreUrl, androidStoreUrl, isStaging, encodedAndroidReferrer, copyToClipboard]);

  const handleStoreClick = useCallback(
    (store: "ios" | "android") => {
      if (store === "ios") {
        copyToClipboard(fullDeepLink);
        window.open(iosStoreUrl, "_blank");
      } else {
        if (isStaging) {
          window.open(androidStoreUrl, "_blank");
        } else {
          window.open(`${androidStoreUrl}&referrer=${encodedAndroidReferrer}`, "_blank");
        }
      }
    },
    [fullDeepLink, iosStoreUrl, androidStoreUrl, isStaging, encodedAndroidReferrer, copyToClipboard]
  );

  // Auto-attempt: open app (deep links) or store (download landing) once on mobile
  useEffect(() => {
    if (device === "desktop" || hasAttempted.current) {
      return;
    }
    hasAttempted.current = true;
    const timer = setTimeout(() => {
      if (mobileOpenTarget === "store") {
        setIsRedirecting(true);
        navigateToStore();
      } else {
        handleOpenApp();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [device, mobileOpenTarget, handleOpenApp, navigateToStore]);

  const storeName = device === "ios" ? "App Store" : "Google Play";
  const showMobileBanner = device !== "desktop" && !hideMobileBanner;
  const showStoreButtonsOnMobile = mobileOpenTarget === "store";

  return (
    <div className={`deep-link-page ${showMobileBanner ? "deep-link-page--with-banner" : ""}`}>
      {showMobileBanner && (
        <div className="deep-link-banner">
          <img
            className="deep-link-banner__icon"
            src={logoYellow}
            alt="FAM"
          />
          <div className="deep-link-banner__text">
            <span className="deep-link-banner__name">FAM</span>
            <span className="deep-link-banner__subtitle">Lifestyle</span>
            <span className="deep-link-banner__store">Free - {storeName}</span>
          </div>
          <button
            className="deep-link-banner__get"
            onClick={navigateToStore}
          >
            GET
          </button>
        </div>
      )}

      <div className="deep-link-container">
        <div className="deep-link-logo">
          <img src={logoYellow} alt="FAM" />
        </div>

        <h1 className="deep-link-title">{config.title}</h1>

        <p className="deep-link-subtitle">
          {device === "desktop"
            ? config.subtitleDesktop
            : isRedirecting
            ? config.subtitleRedirecting
            : config.subtitle}
        </p>

        {device === "desktop" ? (
          <div className="deep-link-desktop">
            <div className="deep-link-qr">
              <QRCodeSVG value={fullDeepLink} size={200} level="M" />
              <p className="deep-link-qr-label">Scan with your phone</p>
            </div>

            <div className="deep-link-divider">
              <span>or download the app</span>
            </div>

            <div className="deep-link-stores">
              <button
                onClick={() => handleStoreClick("ios")}
                className="deep-link-store-btn"
                aria-label="Download on the App Store"
              >
                <DownloadAppStore color={undefined} style={{ height: 48 }} />
              </button>
              <button
                onClick={() => handleStoreClick("android")}
                className="deep-link-store-btn"
                aria-label="Get it on Google Play"
              >
                <DownloadGoogle color={undefined} style={{ height: 48 }} />
              </button>
            </div>
          </div>
        ) : showStoreButtonsOnMobile ? (
          <div className="deep-link-mobile">
            <div className="deep-link-stores">
              <button
                onClick={() => handleStoreClick("ios")}
                className="deep-link-store-btn"
                aria-label="Download on the App Store"
              >
                <DownloadAppStore color={undefined} style={{ height: 48 }} />
              </button>
              <button
                onClick={() => handleStoreClick("android")}
                className="deep-link-store-btn"
                aria-label="Get it on Google Play"
              >
                <DownloadGoogle color={undefined} style={{ height: 48 }} />
              </button>
            </div>
          </div>
        ) : (
          <div className="deep-link-mobile">
            <button
              className="deep-link-open-btn"
              onClick={handleOpenApp}
              disabled={isRedirecting}
            >
              {isRedirecting ? "Opening..." : "Open FAM App"}
            </button>
          </div>
        )}

        <p className="deep-link-info">{config.infoText}</p>
      </div>
    </div>
  );
};

// Export specific page components for each route
export const CircleJoinPage: React.FC = () => <DeepLinkPage linkType="circle-join" />;
export const AccountAccessPage: React.FC = () => <DeepLinkPage linkType="account-access" />;
export const ActivityPage: React.FC = () => <DeepLinkPage linkType="activity" />;
export const NotesPage: React.FC = () => <DeepLinkPage linkType="notes" />;
export const HealthRecordPage: React.FC = () => <DeepLinkPage linkType="health-record" />;
export const DownloadFamPage: React.FC = () => <DeepLinkPage linkType="app-download" />;

export default DeepLinkPage;
